import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { INSIGHTS } from "@/lib/insights";
import { SERVICES } from "@/lib/data";
import { useLang } from "@/lib/LangContext";
import Seo from "@/components/Seo";
import QuoteForm from "@/components/QuoteForm";

export default function InsightsDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const article = INSIGHTS.find((a) => a.slug === slug);
  if (!article) return <Navigate to="/insights" replace />;

  const related = (article.related || [])
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean);

  const more = INSIGHTS.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`page-insight-${article.slug}`}>
      <Seo
        title={`${article.title} | Maze Security Insights`}
        description={article.excerpt}
        path={`/insights/${article.slug}`}
        type="article"
      />
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 border-b border-border overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={article.hero} alt="" className="w-full h-full object-cover grayscale contrast-125" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <Link
            to="/insights"
            className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground hover:text-primary transition-colors mb-8 inline-block"
          >
            ← {lang === "fr" ? "Tous les articles" : "All Articles"}
          </Link>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6 flex flex-wrap gap-4">
            <span>{article.kicker}</span><span>·</span><span>{article.date}</span><span>·</span><span>{article.read}</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
            className="font-serif font-light text-4xl md:text-7xl leading-[1] tracking-tight max-w-4xl"
          >
            {article.title}
          </motion.h1>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-8 space-y-6">
            <p className="font-serif italic text-2xl md:text-3xl leading-snug text-foreground/90 max-w-2xl">
              {article.excerpt}
            </p>
            {article.body.map((p, i) => (
              <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/85 max-w-2xl">
                {p}
              </p>
            ))}
          </div>
          <aside className="md:col-span-4">
            <div className="md:sticky md:top-32 border border-border p-6 bg-[hsl(0_0%_6%)]">
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-4">
                [ {lang === "fr" ? "Services connexes" : "Related Services"} ]
              </div>
              <ul className="space-y-3">
                {related.map((s) => (
                  <li key={s.slug} className="border-b border-border pb-3">
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`insight-related-${s.slug}`}
                      className="font-serif text-xl hover:text-primary transition-colors"
                    >
                      {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact#quote"
                className="mt-8 inline-flex items-center gap-3 bg-primary text-primary-foreground px-5 py-3 font-mono text-xs tracking-[0.2em] uppercase hover:bg-foreground hover:text-background transition-colors"
              >
                {lang === "fr" ? "Demander un devis" : "Request Quote"} <ArrowRight size={14} />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-20">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
            [ {lang === "fr" ? "Continuer la lecture" : "Continue Reading"} ]
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-t border-l border-border">
            {more.map((a) => (
              <Link
                key={a.slug}
                to={`/insights/${a.slug}`}
                data-testid={`insight-more-${a.slug}`}
                className="border-r border-b border-border p-8 hover:bg-[hsl(0_0%_7%)] transition-colors group"
              >
                <div className="font-mono text-[10px] tracking-widest text-muted-foreground mb-3">
                  {a.tag} · {a.read}
                </div>
                <h4 className="font-serif text-2xl leading-tight group-hover:text-primary transition-colors">
                  {a.title}
                </h4>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <QuoteForm />
    </div>
  );
}
